import { useState } from 'react'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent } from '@/components/ui/card'
import { CollectedAtLine } from '@/components/SnapshotCard'
import { CollapsePanel } from '@/components/ui/CollapsePanel'
import { Skeleton } from '@/components/ui/skeleton'
import {
  M365_SNAPSHOT_SLUGS,
  M365_TAB_DESCRIPTION,
  m365SnapshotTitle,
  snapshotByM365Slug,
} from '@/lib/microsoft365Scripts'
import {
  assessmentChecks,
  checkStatusLabel,
  checkStatusVariant,
  normaliseToolkitStatus,
  toolkitStatusLabel,
} from '@/lib/toolkitHealth'

function formatDate(iso) {
  if (!iso) return '—'
  return new Date(iso).toLocaleString('en-GB')
}

function formatEnumLabel(value) {
  if (value == null || value === '') return '—'
  return String(value)
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/_/g, ' ')
    .toLowerCase()
    .replace(/\b\w/g, (c) => c.toUpperCase())
}

function SnapshotField({ label, value }) {
  const text = value == null || value === '' ? '—' : String(value)
  return (
    <div className="flex justify-between gap-4 text-sm">
      <span className="text-muted-foreground">{label}</span>
      <span className="text-right font-medium">{text}</span>
    </div>
  )
}

function ChecksTable({ checks }) {
  const rows = Array.isArray(checks) ? checks : []
  if (rows.length === 0) return null

  return (
    <div className="pt-3">
      <p className="text-xs font-medium text-muted-foreground mb-2">Checks</p>
      <div className="rounded-md border border-border">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border bg-muted/40 text-left text-xs text-muted-foreground">
              <th className="px-3 py-2 font-medium">Area</th>
              <th className="px-3 py-2 font-medium">Status</th>
              <th className="px-3 py-2 font-medium">Detail</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((check, index) => (
              <tr
                key={`${check.area}-${index}`}
                className="border-b border-border last:border-0 align-top"
              >
                <td className="px-3 py-2 font-medium">{check.area || '—'}</td>
                <td className="px-3 py-2 whitespace-nowrap">
                  <Badge variant={checkStatusVariant(check.status)}>
                    {checkStatusLabel(check.status)}
                  </Badge>
                </td>
                <td className="px-3 py-2 text-muted-foreground">{check.detail || '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

function statusPillVariant(status) {
  const normalised = normaliseToolkitStatus(status)
  if (normalised === 'PASS') return 'ready'
  if (normalised === 'WARN') return 'pending'
  if (normalised === 'FAIL') return 'destructive'
  return 'inactive'
}

function M365SnapshotCard({ slug, title, collectedAt, data, open, onOpenChange, children }) {
  const checks = assessmentChecks(data)
  const collapsedSummary = [
    data?.interpretation,
    collectedAt ? `Collected ${formatDate(collectedAt)}` : null,
  ]
    .filter(Boolean)
    .join(' · ')

  return (
    <CollapsePanel
      id={`m365-${slug}`}
      title={title}
      collapsedSummary={collapsedSummary || 'Expand for details'}
      open={open}
      onOpenChange={onOpenChange}
      pill={data?.overall_status ? toolkitStatusLabel(data.overall_status) : undefined}
      pillVariant={statusPillVariant(data?.overall_status)}
    >
      <CollectedAtLine collectedAt={collectedAt} className="mb-3" />
      {data?.interpretation && (
        <p className="text-sm text-muted-foreground mb-3">{data.interpretation}</p>
      )}
      <div className="space-y-2">
        {children}
        <ChecksTable checks={checks} />
      </div>
    </CollapsePanel>
  )
}

function OneDriveCard({ snapshot, open, onOpenChange }) {
  if (!snapshot) return null
  const data = snapshot.payload || {}
  const accounts = Array.isArray(data.accounts) ? data.accounts : []

  return (
    <M365SnapshotCard
      slug={snapshot.script_slug}
      title={m365SnapshotTitle(snapshot.script_slug, snapshot.script_title)}
      collectedAt={snapshot.collected_at}
      data={data}
      open={open}
      onOpenChange={onOpenChange}
    >
      {data.scanned_users?.length > 0 && (
        <SnapshotField label="Scanned users" value={data.scanned_users.join(', ')} />
      )}
      {data.run_context && (
        <SnapshotField label="Run context" value={formatEnumLabel(data.run_context)} />
      )}
      <SnapshotField
        label="Client installed"
        value={data.client_installed === true ? 'Yes' : data.client_installed === false ? 'No' : null}
      />
      <SnapshotField label="Client version" value={data.client_version} />
      <SnapshotField
        label="Sync process running"
        value={
          data.process_running === true ? 'Yes' : data.process_running === false ? 'No' : null
        }
      />
      <SnapshotField label="Configured accounts" value={data.account_count ?? accounts.length} />
      <SnapshotField label="Files on-demand" value={data.files_on_demand} />

      {accounts.length > 0 ? (
        <div className="pt-3">
          <p className="text-xs font-medium text-muted-foreground mb-2">Accounts</p>
          <div className="rounded-md border border-border">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border bg-muted/40 text-left text-xs text-muted-foreground">
                  <th className="px-3 py-2 font-medium">User</th>
                  <th className="px-3 py-2 font-medium">Account</th>
                  <th className="px-3 py-2 font-medium">Sync state</th>
                  <th className="px-3 py-2 font-medium text-right">Library size</th>
                  <th className="px-3 py-2 font-medium text-right">Drive free</th>
                </tr>
              </thead>
              <tbody>
                {accounts.map((account, index) => (
                  <tr
                    key={`${account.email}-${index}`}
                    className="border-b border-border last:border-0"
                  >
                    <td className="px-3 py-2">{account.user_label || '—'}</td>
                    <td className="px-3 py-2 font-medium">{account.email || '—'}</td>
                    <td className="px-3 py-2">{account.sync_state || '—'}</td>
                    <td className="px-3 py-2 text-right tabular-nums">
                      {account.size_mb != null ? `${account.size_mb} MB` : '—'}
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums">
                      {account.drive_free_gb != null ? `${account.drive_free_gb} GB` : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        <p className="text-sm text-muted-foreground pt-1">
          No OneDrive accounts reported. If this device has M365 users, run the script as the
          logged-on user or when a profile is loaded.
        </p>
      )}
    </M365SnapshotCard>
  )
}

function TeamsCard({ snapshot, open, onOpenChange }) {
  if (!snapshot) return null
  const data = snapshot.payload || {}

  return (
    <M365SnapshotCard
      slug={snapshot.script_slug}
      title={m365SnapshotTitle(snapshot.script_slug, snapshot.script_title)}
      collectedAt={snapshot.collected_at}
      data={data}
      open={open}
      onOpenChange={onOpenChange}
    >
      <SnapshotField label="Client type" value={formatEnumLabel(data.client_type)} />
      <SnapshotField
        label="Client installed"
        value={data.client_installed === true ? 'Yes' : data.client_installed === false ? 'No' : null}
      />
      <SnapshotField label="Client version" value={data.client_version} />
      <SnapshotField
        label="Process running"
        value={
          data.process_running === true ? 'Yes' : data.process_running === false ? 'No' : null
        }
      />
      <SnapshotField
        label="Process responding"
        value={
          data.process_responding === true
            ? 'Yes'
            : data.process_responding === false
              ? 'No'
              : null
        }
      />
      <SnapshotField label="WebView2 version" value={data.webview2_version} />
      <SnapshotField
        label="Largest cache"
        value={data.cache_size_mb != null ? `${data.cache_size_mb} MB` : null}
      />
    </M365SnapshotCard>
  )
}

function SharePointCard({ snapshot, open, onOpenChange }) {
  if (!snapshot) return null
  const data = snapshot.payload || {}
  const libraries = Array.isArray(data.libraries) ? data.libraries : []

  return (
    <M365SnapshotCard
      slug={snapshot.script_slug}
      title={m365SnapshotTitle(snapshot.script_slug, snapshot.script_title)}
      collectedAt={snapshot.collected_at}
      data={data}
      open={open}
      onOpenChange={onOpenChange}
    >
      {data.scanned_users?.length > 0 && (
        <SnapshotField label="Scanned users" value={data.scanned_users.join(', ')} />
      )}
      {data.run_context && (
        <SnapshotField label="Run context" value={formatEnumLabel(data.run_context)} />
      )}
      <SnapshotField
        label="OneDrive sync client"
        value={
          data.onedrive_client_present === true
            ? 'Present'
            : data.onedrive_client_present === false
              ? 'Not found'
              : null
        }
      />
      <SnapshotField
        label="Sync process running"
        value={
          data.process_running === true ? 'Yes' : data.process_running === false ? 'No' : null
        }
      />
      <SnapshotField label="Synced libraries" value={data.library_count ?? libraries.length} />
      <SnapshotField
        label="Sync errors (24h)"
        value={data.sync_errors_24h != null ? String(data.sync_errors_24h) : null}
      />

      {libraries.length > 0 ? (
        <div className="pt-3">
          <p className="text-xs font-medium text-muted-foreground mb-2">Libraries</p>
          <div className="rounded-md border border-border">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border bg-muted/40 text-left text-xs text-muted-foreground">
                  <th className="px-3 py-2 font-medium">Library</th>
                  <th className="px-3 py-2 font-medium">Account</th>
                  <th className="px-3 py-2 font-medium">Sync state</th>
                  <th className="px-3 py-2 font-medium">Path</th>
                </tr>
              </thead>
              <tbody>
                {libraries.map((library, index) => (
                  <tr
                    key={`${library.name}-${index}`}
                    className="border-b border-border last:border-0 align-top"
                  >
                    <td className="px-3 py-2 font-medium">{library.name || '—'}</td>
                    <td className="px-3 py-2">{library.account || '—'}</td>
                    <td className="px-3 py-2">{library.sync_state || '—'}</td>
                    <td className="px-3 py-2 text-muted-foreground break-all">
                      {library.path || library.url || '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        <p className="text-sm text-muted-foreground pt-1">
          No SharePoint library sync configuration found. This is normal if libraries are used
          online only, or if the script ran without a loaded user profile.
        </p>
      )}
    </M365SnapshotCard>
  )
}

function EmptyM365Card({ slug, open, onOpenChange }) {
  return (
    <CollapsePanel
      id={`m365-${slug}-empty`}
      title={m365SnapshotTitle(slug)}
      collapsedSummary={`No data yet · deploy ${slug} from Atera`}
      open={open}
      onOpenChange={onOpenChange}
      pill="No data"
      pillVariant="inactive"
      className="border-dashed"
    >
      <p className="text-sm text-muted-foreground">
        No data yet. Deploy the {slug} RMM script from Atera to populate this section.
      </p>
    </CollapsePanel>
  )
}

function cardForSlug(slug, snapshot, open, onOpenChange) {
  if (!snapshot) return <EmptyM365Card key={slug} slug={slug} open={open} onOpenChange={onOpenChange} />
  if (slug === 'onedrive-health') {
    return <OneDriveCard key={slug} snapshot={snapshot} open={open} onOpenChange={onOpenChange} />
  }
  if (slug === 'teams-health') {
    return <TeamsCard key={slug} snapshot={snapshot} open={open} onOpenChange={onOpenChange} />
  }
  if (slug === 'sharepoint-sync-health') {
    return <SharePointCard key={slug} snapshot={snapshot} open={open} onOpenChange={onOpenChange} />
  }
  return null
}

export function AgentMicrosoft365({ loading, data }) {
  const [openCards, setOpenCards] = useState(() =>
    Object.fromEntries(M365_SNAPSHOT_SLUGS.map((slug) => [slug, false])),
  )

  function setCardOpen(slug, open) {
    setOpenCards((prev) => ({ ...prev, [slug]: open }))
  }

  if (loading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-16 w-full" />
        <Skeleton className="h-48 w-full" />
        <Skeleton className="h-48 w-full" />
      </div>
    )
  }

  const snapshots = data?.snapshots ?? []
  const hasAny = M365_SNAPSHOT_SLUGS.some((slug) => snapshotByM365Slug(snapshots, slug))

  return (
    <div className="space-y-6">
      <p className="text-sm text-muted-foreground">{M365_TAB_DESCRIPTION}</p>

      {!hasAny && (
        <Card className="border-dashed">
          <CardContent className="pt-6 text-sm text-muted-foreground">
            No Microsoft 365 health data for this device yet. Deploy the onedrive-health,
            teams-health, and sharepoint-sync-health scripts from Admin → RMM ingest status.
          </CardContent>
        </Card>
      )}

      <div className="space-y-4">
        {M365_SNAPSHOT_SLUGS.map((slug) =>
          cardForSlug(
            slug,
            snapshotByM365Slug(snapshots, slug),
            openCards[slug] ?? false,
            (open) => setCardOpen(slug, open),
          ),
        )}
      </div>
    </div>
  )
}
